import {
  Button,
  Card,
  Checkbox,
  Input,
  InputError,
  InputSelect,
  Label,
  MultiSelect,
} from '@features/ui';
import { Textarea } from '@features/ui/components/textarea';
import { FC, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { UpdateUserReq, UserProfile } from '@src/types/users.ts';
import { resendSignUpEmail, updateBrowseUser } from '@src/services/users.ts';
import { TOption } from '@src/types';
import { useAuthStore } from '@src/stores/authStore';
import { ChevronDown, ChevronUp } from 'lucide-react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { toast } from 'react-toastify';
import { getUserGroups } from '@src/services/userGroups';
import { UserGroup } from '@src/types/userGroups';

import { UserGroupsPopup } from './UserGroupsPopup';

interface UsersFormProps {
  user: UserProfile | null;
  countryOptions: TOption[];
  stateOptions: TOption[];
}

const validationSchema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup
    .string()
    .optional()
    .matches(/^(?=.*[a-z])/, 'Must contain at least one lowercase letter')
    .matches(/^(?=.*[A-Z])/, 'Must contain at least one uppercase letter')
    .matches(/^(?=.*\d)/, 'Must contain at least one number')
    .matches(
      /^(?=.*[@$!%*?&^#()\-+=_{}[\]:;<>.,/\\|`~])/,
      'Must contain at least one special character'
    )
    .matches(/^[^\s]+$/, 'Must not contain spaces')
    .when({
      is: (val: string) => !!val,
      then: (schema) =>
        schema
          .min(8, 'Password must be at least 8 characters long')
          .max(16, 'Password must be at most 16 characters long'),
      otherwise: (schema) => schema,
    })
    .nullable(),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  is_locked: yup.boolean(), // 'no' | 'yes' | 'disable'
  isApproved: yup.boolean(),
  unsubscribed: yup.boolean(),
  groups: yup.array().of(yup.string()).optional(),
  phoneNumber: yup.string().optional(),
  lang: yup.string().optional(),
  businessname: yup.string().optional(),
  street: yup.string().optional(),
  city: yup.string().optional(),
  state: yup.string().optional(),
  country: yup.string().optional(),
  zip: yup.string().optional(),
  internalNotes: yup.string().optional(),
  referral_source: yup.string().optional(),
});

type FormData = yup.InferType<typeof validationSchema>;

const languageOptions: TOption[] = [
  { value: 'finnish', label: 'Finnish' },
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish' },
];

export const UsersForm: FC<UsersFormProps> = ({
  user,
  countryOptions,
  stateOptions,
}) => {
  const { auth } = useAuthStore();
  const isAdmin = useMemo(
    () => auth?.user?.role === 'admin',
    [auth?.user?.role]
  );

  const [isAddressOpen, setIsAddressOpen] = useState(false);
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [isGroupsPopupOpen, setIsGroupsPopupOpen] = useState(false);
  const [userGroupOptions, setUserGroupOptions] = useState<TOption[]>([]);

  // Fetch user groups để hiển thị trong MultiSelect
  useEffect(() => {
    fetchUserGroups();
  }, []);

  const fetchUserGroups = async () => {
    try {
      const response = await getUserGroups();
      const options: TOption[] = (response.data || []).map(
        (group: UserGroup) => ({
          value: group.id.toString(),
          label: group.name,
        })
      );

      setUserGroupOptions(options);
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      username: '',
      password: undefined,
      firstName: '',
      lastName: '',
      email: '',
      referral_source: '',
      is_locked: false,
      isApproved: false,
      unsubscribed: false,
      groups: [],
      phoneNumber: '',
      lang: 'finnish',
      businessname: '',
      street: '',
      city: '',
      state: '',
      country: '',
      zip: '',
      internalNotes: '',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        username: user?.username ?? '',
        password: undefined,
        firstName: user?.first_name ?? '',
        lastName: user?.last_name ?? '',
        referral_source: user?.referral_source ?? '',
        email: user?.email ?? '',
        is_locked: user?.is_locked === 1,
        isApproved: user?.is_approved === 1,
        unsubscribed: user?.unsubscribed === 1,
        groups: user?.group_ids?.map((id) => id.toString()) ?? [],
        phoneNumber: user?.phone_number ?? '',
        lang: user?.lang ?? '',
        businessname: user?.businessname ?? '',
        street: user?.street ?? '',
        city: user?.city ?? '',
        state: user?.state ?? '',
        country: user?.country ?? '',
        zip: user?.zip ?? '',
        internalNotes: user?.internal_notes ?? '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: FormData) => {
    if (!user?.id) return;

    const payload: UpdateUserReq = {
      email: data.email || '',
      password: data.password || undefined,
      first_name: data.firstName || '',
      last_name: data.lastName || '',
      phone_number: data.phoneNumber || '',
      referral_source: data.referral_source || '',
      internal_notes: data.internalNotes || '',
      is_locked: data.is_locked,
      is_approved: data.isApproved,
      unsubscribed: data.unsubscribed,
      groups: data.groups?.map((groupId) => Number(groupId)) || [],
      lang: data.lang || '',
      country: data.country || '',
      street: data.street || '',
      city: data.city || '',
      state: data.state || '',
      zip: data.zip || '',
      // username: data.username || '',
      businessname: data.businessname || '',
    };

    try {
      await updateBrowseUser(user.id, payload);
      reset(data);
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleResendSignUpEmail = async () => {
    if (!user?.id) return;
    try {
      const res = await resendSignUpEmail(user.id);

      toast.success(res.data?.message || 'Signup email resent successfully');
    } catch {
      toast.error('Failed to resend signup email');
    }
  };

  return (
    <Card className={'flex-1 p-6'}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* General Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">General</h3>

          {/* Username */}
          <div className="mb-4">
            <Label className="text-sm mb-2">* Username</Label>
            <Controller
              control={control}
              name="username"
              render={({ field }) => (
                <Input
                  {...field}
                  error={!!errors.username}
                  readOnly={!isAdmin}
                  textSize="sm"
                  variant="secondary"
                />
              )}
            />
            {errors.username && (
              <InputError>{errors.username.message}</InputError>
            )}
          </div>

          {/* Comment */}
          <div className="mb-4">
            <Label className="text-sm mb-2">Comment</Label>
            <Controller
              control={control}
              name="internalNotes"
              render={({ field }) => (
                <>
                  {!isCommentOpen && !field.value ? (
                    <div
                      className="text-sm text-blue-400 cursor-pointer hover:underline"
                      onClick={() => setIsCommentOpen(true)}
                    >
                      Add Comment
                    </div>
                  ) : (
                    <Textarea
                      {...field}
                      hiddenHeader
                      className="min-h-[80px]"
                      placeholder="Write your comment..."
                    />
                  )}
                </>
              )}
            />
            <div className="text-xs text-gray-500 mt-1">for admin purposes</div>
          </div>

          {/* Password */}
          <div className="mb-4">
            <Label className="text-sm mb-2">Password</Label>
            <Controller
              control={control}
              name="password"
              render={({ field }) => (
                <Input
                  {...field}
                  autoComplete={'new-password'}
                  error={!!errors.password}
                  placeholder="(change)"
                  textSize="sm"
                  type="password"
                  value={field.value ?? ''}
                  variant="secondary"
                />
              )}
            />
            {errors.password?.message && (
              <InputError>{errors.password.message}</InputError>
            )}
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-sm mb-2">First and Last name</Label>
              <Controller
                control={control}
                name="firstName"
                render={({ field }) => (
                  <Input
                    {...field}
                    error={!!errors.firstName}
                    readOnly={!isAdmin}
                    textSize="sm"
                    variant="secondary"
                  />
                )}
              />
              {errors.firstName?.message && (
                <InputError>{errors.firstName.message}</InputError>
              )}
            </div>
            <div>
              <Label className="text-sm mb-2">&nbsp;</Label>
              <Controller
                control={control}
                name="lastName"
                render={({ field }) => (
                  <Input
                    {...field}
                    error={!!errors.lastName}
                    readOnly={!isAdmin}
                    textSize="sm"
                    variant="secondary"
                  />
                )}
              />
              {errors.lastName?.message && (
                <InputError>{errors.lastName.message}</InputError>
              )}
            </div>
          </div>

          {/* E-Mail Address */}
          <div className="mb-4">
            <Label className="text-sm mb-2">* E-Mail Address</Label>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <Input
                  {...field}
                  error={!!errors.email}
                  readOnly={!isAdmin}
                  textSize="sm"
                  variant="secondary"
                />
              )}
            />
            {errors.email?.message && (
              <InputError>{errors.email.message}</InputError>
            )}
            <div className="flex items-center gap-2 mt-2">
              <Button
                className="text-xs"
                size="sm"
                type="button"
                variant="outline"
                onClick={handleResendSignUpEmail}
              >
                Resend Signup E-Mail
              </Button>
            </div>
          </div>

          {/* Permanently Locked - Radio buttons */}
          <div className="mb-4">
            <Label className="text-sm mb-2 block">Permanently Locked</Label>
            <Controller
              control={control}
              name="is_locked"
              render={({ field }) => (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      checked={!field.value}
                      className="cursor-pointer"
                      id="locked-no"
                      name="is_locked"
                      type="radio"
                      value="no"
                      onChange={() => field.onChange(false)}
                    />
                    <label
                      className="text-sm cursor-pointer"
                      htmlFor="locked-no"
                    >
                      No
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      checked={field.value}
                      className="cursor-pointer"
                      id="locked-yes"
                      name="is_locked"
                      type="radio"
                      value="yes"
                      onChange={() => field.onChange(true)}
                    />
                    <label
                      className="text-sm cursor-pointer text-red-500"
                      htmlFor="locked-yes"
                    >
                      Yes, locked
                    </label>
                  </div>
                </div>
              )}
            />
          </div>

          {/* Is Approved - Checkbox */}
          <div className="mb-4">
            <Label className="text-sm mb-2 block">Is Approved</Label>
            <Controller
              control={control}
              name="isApproved"
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <Label className="text-sm cursor-pointer">Approved</Label>
                </div>
              )}
            />
          </div>

          {/* Is Unsubscribed - Radio buttons */}
          <div className="mb-4">
            <Label className="text-sm mb-2 block">Is Unsubscribed?</Label>
            <Controller
              control={control}
              name="unsubscribed"
              render={({ field }) => (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      checked={!field.value}
                      className="cursor-pointer"
                      id="unsub-no"
                      name="unsubscribed"
                      type="radio"
                      value="no"
                      onChange={() => field.onChange(false)}
                    />
                    <label
                      className="text-sm cursor-pointer"
                      htmlFor="unsub-no"
                    >
                      No
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      checked={field.value}
                      className="cursor-pointer"
                      id="unsub-yes"
                      name="unsubscribed"
                      type="radio"
                      value="yes"
                      onChange={() => field.onChange(true)}
                    />
                    <label
                      className="text-sm cursor-pointer"
                      htmlFor="unsub-yes"
                    >
                      Yes, do not e-mail this customer for any reasons
                    </label>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 ml-6">
                    if enabled, this will unsubscribe the customer from:
                    <br />* messages that you send from aMember Cp,
                    <br />* autoresponder messages,
                    <br />* subscription expiration notices
                  </div>
                </div>
              )}
            />
          </div>

          {/* User Groups */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">User Groups</Label>
              <Button
                className="text-xs h-7"
                size="sm"
                type="button"
                variant="outline"
                onClick={() => setIsGroupsPopupOpen(true)}
              >
                Edit Groups
              </Button>
            </div>
            <Controller
              control={control}
              name="groups"
              render={({ field }) => (
                <MultiSelect
                  options={userGroupOptions}
                  placeholder="-- Please Select --"
                  value={field.value as (string | number)[] | null | undefined}
                  onChange={(value) => field.onChange(value)}
                />
              )}
            />
          </div>

          {/* Phone Numbers */}
          <div className="mb-4">
            <Label className="text-sm mb-2">Phone Number</Label>
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <Input {...field} textSize="sm" variant="secondary" />
              )}
            />
          </div>

          {/* Preferred Language */}
          <div className="mb-4">
            <Label className="text-sm mb-2">
              Preferred Language
              <span className="text-xs text-gray-500 ml-2">
                (What&lsquo;s your preferred language?)
              </span>
            </Label>
            <Controller
              control={control}
              name="lang"
              render={({ field }) => (
                <InputSelect
                  className="w-full"
                  options={languageOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* How did you hear of Leeloo */}
          <div className="mb-4">
            <Label className="text-sm mb-2">How did you hear of Leeloo?</Label>
            <Controller
              control={control}
              name="referral_source"
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="e.g. Google search, friend recommendation, etc."
                  textSize="sm"
                  variant="secondary"
                />
              )}
            />
          </div>

          {/* Business Name */}
          <div className="mb-4">
            <Label className="text-sm mb-2">Business Name</Label>
            <Controller
              control={control}
              name="businessname"
              render={({ field }) => (
                <Input {...field} textSize="sm" variant="secondary" />
              )}
            />
            <p className="text-xs text-gray-500 mt-1">
              This will ONLY add your business name to your invoice. If you want
              your business name added to your accounts you must contact us at
              www.leelootrading.com/contact.
            </p>
          </div>
        </div>

        {/* Address Info Collapsible */}
        <CollapsiblePrimitive.Root
          className="mb-6"
          open={isAddressOpen}
          onOpenChange={setIsAddressOpen}
        >
          <CollapsiblePrimitive.Trigger asChild>
            <button
              className="flex items-center justify-between w-full text-left"
              type="button"
            >
              <h3 className="text-lg font-semibold cursor-pointer hover:text-blue-400">
                + Address Info...
              </h3>
              {isAddressOpen ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </CollapsiblePrimitive.Trigger>
          <CollapsiblePrimitive.Content className="mt-4 space-y-4">
            <div>
              <Label className="text-sm mb-2">Street</Label>
              <Controller
                control={control}
                name="street"
                render={({ field }) => (
                  <Input {...field} textSize="sm" variant="secondary" />
                )}
              />
            </div>
            <div>
              <Label className="text-sm mb-2">City</Label>
              <Controller
                control={control}
                name="city"
                render={({ field }) => (
                  <Input {...field} textSize="sm" variant="secondary" />
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-2">State</Label>
                <Controller
                  control={control}
                  name="state"
                  render={({ field }) => (
                    <InputSelect
                      className="w-full"
                      options={stateOptions}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div>
                <Label className="text-sm mb-2">Zip Code</Label>
                <Controller
                  control={control}
                  name="zip"
                  render={({ field }) => (
                    <Input {...field} textSize="sm" variant="secondary" />
                  )}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm mb-2">Country</Label>
              <Controller
                control={control}
                name="country"
                render={({ field }) => (
                  <InputSelect
                    className="w-full"
                    options={countryOptions}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </CollapsiblePrimitive.Content>
        </CollapsiblePrimitive.Root>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button size="sm" type="submit" variant="lime">
            Save
          </Button>
        </div>
      </form>

      <UserGroupsPopup
        open={isGroupsPopupOpen}
        onGroupsChange={fetchUserGroups}
        onOpenChange={setIsGroupsPopupOpen}
      />
    </Card>
  );
};
